/**
 * useAutoTopUp
 *
 * Reads the OLV wallet auto top-up settings at `users/{uid}/wallet/autoTopUp`
 * (real-time, onSnapshot) and changes them through the server: the
 * walletAutoTopUpSettings callable is the only writer (SOC 2 decision 1,
 * part 2, 2026-10-18 — functions/src/wallet/autoTopUpSettings.ts). The page
 * sends the action and the terms the person chose; the server sets the
 * approved ceiling equal to the amount, keeps the 24-hour charge cap, takes
 * the card's digits from Stripe after checking the card is theirs, and
 * records the consent. The rules refuse a direct write (they have since
 * 2026-09-15).
 *
 * The settings UI consumes `enable(opts)` / `disable()` / `update(opts)`.
 * `confirmedAmountUsd`, `cardLast4` and `maxChargesPer24h` in `opts` are no
 * longer sent: the server owns them.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import db from '../utils/firestore';
import {
  AutoTopUpSettings,
  AutoTopUpUpdate,
  AUTO_TOP_UP_DEFAULTS,
} from '../types/autoTopUp';
import type { AutoTopUpAction } from '../constants/walletVocabulary';

export interface UseAutoTopUpResult {
  /** Current settings (defaults until the doc loads / when absent). */
  settings: AutoTopUpSettings;
  /**
   * True once a settings doc exists in Firestore — i.e. the user has made an
   * explicit auto top-up choice at some point (enabled OR disabled). False for
   * never-configured users, which is what lets the top-up modal offer
   * default-on enrollment without ever overriding a stored "off".
   */
  hasStoredSettings: boolean;
  /** True while the initial snapshot is in flight. */
  isLoading: boolean;
  /** Non-null when the listener or a write failed. */
  error: string | null;
  /** Turn auto top-up on, optionally overriding any adjustable fields. */
  enable: (opts?: AutoTopUpUpdate) => Promise<void>;
  /** Turn auto top-up off (preserves the other settings). */
  disable: () => Promise<void>;
  /** Patch one or more adjustable fields without flipping `enabled`. */
  update: (opts: AutoTopUpUpdate) => Promise<void>;
}

function toNumber(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : fallback;
}

/** Coerce a raw Firestore doc into a fully-populated settings object. */
function normalize(data: any): AutoTopUpSettings {
  return {
    enabled: data?.enabled === true,
    thresholdUsd: toNumber(data?.thresholdUsd, AUTO_TOP_UP_DEFAULTS.thresholdUsd),
    topUpAmountUsd: toNumber(data?.topUpAmountUsd, AUTO_TOP_UP_DEFAULTS.topUpAmountUsd),
    maxChargesPer24h: Math.max(
      0,
      Math.floor(toNumber(data?.maxChargesPer24h, AUTO_TOP_UP_DEFAULTS.maxChargesPer24h)),
    ),
    paymentMethodId:
      typeof data?.paymentMethodId === 'string' && data.paymentMethodId
        ? data.paymentMethodId
        : undefined,
    cardLast4:
      typeof data?.cardLast4 === 'string' && /^\d{4}$/.test(data.cardLast4)
        ? data.cardLast4
        : undefined,
    updatedAt: data?.updatedAt,
  };
}

/** What the page sends the callable: an action and the terms the person chose. */
interface AutoTopUpRequest {
  action: AutoTopUpAction;
  thresholdUsd?: number;
  topUpAmountUsd?: number;
  paymentMethodId?: string;
}

/** The terms in `opts` the server reads, well-typed; the range checks are the server's. */
function termsOf(opts?: AutoTopUpUpdate): Omit<AutoTopUpRequest, 'action'> {
  const out: Omit<AutoTopUpRequest, 'action'> = {};
  if (!opts) return out;
  if (opts.thresholdUsd !== undefined) {
    const v = toNumber(opts.thresholdUsd, Number.NaN);
    if (Number.isFinite(v)) out.thresholdUsd = Math.max(0, v);
  }
  if (opts.topUpAmountUsd !== undefined) {
    const v = toNumber(opts.topUpAmountUsd, Number.NaN);
    if (Number.isFinite(v)) out.topUpAmountUsd = v;
  }
  if (typeof opts.paymentMethodId === 'string' && opts.paymentMethodId) out.paymentMethodId = opts.paymentMethodId;
  return out;
}

export function useAutoTopUp(uid: string | null | undefined): UseAutoTopUpResult {
  const [settings, setSettings] = useState<AutoTopUpSettings>({ ...AUTO_TOP_UP_DEFAULTS });
  const [hasStoredSettings, setHasStoredSettings] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const unsubRef = useRef<Unsubscribe | null>(null);

  const path = uid ? `users/${uid}/wallet/autoTopUp` : null;

  useEffect(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    if (!path) {
      setSettings({ ...AUTO_TOP_UP_DEFAULTS });
      setHasStoredSettings(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    // try/catch: doc()/onSnapshot throw synchronously when the Firestore
    // client has been terminated (isolated test hosts, teardown races). Fail
    // open to the defaults — same contract as a listener error.
    try {
      unsubRef.current = onSnapshot(
        doc(db, path),
        (snap) => {
          setSettings(normalize(snap.exists() ? snap.data() : null));
          setHasStoredSettings(snap.exists());
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          setError(err.message || 'Failed to load auto top-up settings');
          setIsLoading(false);
        },
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load auto top-up settings');
      setIsLoading(false);
    }
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [path]);

  const write = useCallback(
    async (request: AutoTopUpRequest) => {
      if (!path) throw new Error('Cannot update auto top-up: not signed in');
      try {
        // Loaded on use: the chat imports this hook, and its first download stays as it was.
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const call = httpsCallable<AutoTopUpRequest, unknown>(getFunctions(undefined, 'us-central1'), 'walletAutoTopUpSettings');
        await call(request);
      } catch (e: any) {
        setError(e?.message || 'Failed to update auto top-up settings');
        throw e;
      }
    },
    [path],
  );

  // Turning on states the terms: a threshold or an amount not given stays the one on screen.
  const enable = useCallback(
    (opts?: AutoTopUpUpdate) => write({
      action: 'enable',
      thresholdUsd: settings.thresholdUsd,
      topUpAmountUsd: settings.topUpAmountUsd,
      ...termsOf(opts),
    }),
    [write, settings.thresholdUsd, settings.topUpAmountUsd],
  );
  const disable = useCallback(() => write({ action: 'disable' }), [write]);
  const update = useCallback((opts: AutoTopUpUpdate) => write({ action: 'update', ...termsOf(opts) }), [write]);

  return { settings, hasStoredSettings, isLoading, error, enable, disable, update };
}

export default useAutoTopUp;
